import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaExclamationTriangle, FaRocket, FaMagic, FaWrench } from 'react-icons/fa';
import IconWrapper from './IconWrapper';
import { versionService, VersionComparisonResult } from '../services/VersionService';
import { trackVersionUpdateAccept, trackVersionUpdateDismiss } from '../services/analytics';
import '../styles/VersionUpdateModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  versionInfo: VersionComparisonResult;
}

const VersionUpdateModal: React.FC<Props> = ({ isOpen, onClose, versionInfo }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleUpdate = async () => {
    trackVersionUpdateAccept();
    setIsUpdating(true);
    try {
      await versionService.performUpdate(versionInfo.latestVersion);
      setTimeout(() => versionService.forceReload(), 1000);
    } catch {
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    trackVersionUpdateDismiss();
    onClose();
  };

  const getUpdateTypeText = (type: string) => {
    switch (type) {
      case 'major':
        return '重大更新';
      case 'minor':
        return '功能更新';
      case 'patch':
        return '修復更新';
      default:
        return '更新';
    }
  };

  const getUpdateDescription = (type: string, forced?: boolean) => {
    if (forced) return '您的應用版本過低，必須更新到最新版本才能繼續使用。';
    switch (type) {
      case 'major':
        return '此次更新包含重要的新功能和改進，建議立即更新以獲得最佳體驗。';
      case 'minor':
        return '此次更新包含新功能和優化，建議更新以體驗新功能。';
      case 'patch':
        return '此次更新包含錯誤修復和性能優化，建議更新以提升穩定性。';
      default:
        return '發現新版本，建議更新以獲得最佳體驗。';
    }
  };

  if (!isOpen) return null;

  const content = (
    <div className="version-update-overlay" onClick={versionInfo.forceUpdate ? undefined : handleDismiss}>
      <div
        className="version-update-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="version-update-content">
          <div className="version-update-icon">
            {versionInfo.isMinVersionForced ? (
              <IconWrapper icon={FaExclamationTriangle} size={48} style={{ color: 'var(--warning)' }} />
            ) : versionInfo.updateType === 'major' ? (
              <IconWrapper icon={FaRocket} size={48} style={{ color: 'var(--primary-color)' }} />
            ) : versionInfo.updateType === 'minor' ? (
              <IconWrapper icon={FaMagic} size={48} style={{ color: 'var(--primary-color)' }} />
            ) : (
              <IconWrapper icon={FaWrench} size={48} style={{ color: 'var(--success)' }} />
            )}
          </div>

          <div className="version-update-title">
            {versionInfo.isMinVersionForced ? '必須更新' : getUpdateTypeText(versionInfo.updateType)}
          </div>

          <div className={`version-update-type ${versionInfo.updateType} ${versionInfo.isMinVersionForced ? 'forced' : ''}`}>
            {versionInfo.isMinVersionForced ? '強制更新' : getUpdateTypeText(versionInfo.updateType)}
          </div>

          <div className="version-numbers">
            <span className="current">{versionInfo.currentVersion}</span>
            <span className="arrow">→</span>
            <span className="latest">{versionInfo.latestVersion}</span>
          </div>

          <div className="version-info">
            <h4>{versionInfo.isMinVersionForced ? '重要提醒' : '更新說明'}</h4>
            <p>{getUpdateDescription(versionInfo.updateType, versionInfo.isMinVersionForced)}</p>
          </div>

          <div className="version-update-actions">
            {isUpdating ? (
              <div className="updating-status">
                <div className="updating-spinner" />
                <span>正在更新...</span>
              </div>
            ) : (
              <>
                <button
                  className="update-button primary"
                  onClick={handleUpdate}
                  disabled={isUpdating}
                >
                  {versionInfo.forceUpdate ? '立即更新' : '現在更新'}
                </button>

                {!versionInfo.forceUpdate && (
                  <button className="update-button secondary" onClick={handleDismiss} disabled={isUpdating}>
                    稍後提醒
                  </button>
                )}
              </>
            )}
          </div>

          {!versionInfo.forceUpdate && (
            <div className="update-note">
              <small>下次啟動時會再次檢查版本。</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default VersionUpdateModal;
